import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { brandColors } from '@config/brandTokens';

export interface SkillQuarterPoint {
  key: string;
  label: string;
  score: number | null;
  videoUrl?: string;
}

export interface SkillJourneyItem {
  skillName: string;
  sport: string;
  latestScore: number | null;
  improvement: number | null;
  quarters: SkillQuarterPoint[];
}

const SkillQuarterJourney: React.FC<{ journey: SkillJourneyItem }> = ({ journey }) => {
  const scores = journey.quarters.map((q) => q.score).filter((n): n is number => n != null);
  const max = Math.max(100, ...scores, 1);

  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 1.25,
        px: 1.25,
        py: 1.1,
        bgcolor: '#fff',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: 13.5 }}>
          {journey.skillName}
        </Typography>
        <Typography sx={{ fontWeight: 800, color: brandColors.blue, fontSize: 13 }}>
          {journey.latestScore != null ? journey.latestScore.toFixed(1) : '—'}
          {journey.improvement != null ? (
            <Typography component="span" sx={{ ml: 0.75, fontSize: 11, fontWeight: 700, color: journey.improvement >= 0 ? brandColors.green : '#b91c1c' }}>
              {journey.improvement >= 0 ? '+' : ''}
              {journey.improvement.toFixed(1)}
            </Typography>
          ) : null}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} sx={{ mt: 1.1 }} alignItems="flex-end">
        {journey.quarters.map((quarter, index) => {
          const filled = quarter.score != null;
          const hasVideo = Boolean(quarter.videoUrl);
          const height = filled ? Math.max(18, Math.round((Number(quarter.score) / max) * 42)) : hasVideo ? 16 : 10;
          return (
            <Box key={quarter.key} sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <Box
                sx={{
                  height: 48,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '70%',
                    maxWidth: 28,
                    height,
                    borderRadius: 0.75,
                    bgcolor: filled ? brandColors.blue : hasVideo ? brandColors.violet : '#e2e8f0',
                    background: filled
                      ? index === journey.quarters.length - 1
                        ? `linear-gradient(180deg, ${brandColors.orange} 0%, ${brandColors.blue} 100%)`
                        : brandColors.blue
                      : hasVideo
                        ? brandColors.violet
                        : '#e2e8f0',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: 10, fontWeight: 800, color: brandColors.slate, mt: 0.4, letterSpacing: 0.2 }}>
                {quarter.label.replace(/\s\d{4}$/, '')}
              </Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: filled ? brandColors.navy : '#94a3b8' }}>
                {filled ? Number(quarter.score).toFixed(0) : hasVideo ? '▶' : '–'}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default SkillQuarterJourney;
